import React, { useState, useEffect, useContext } from "react";
import { withStyles } from "@material-ui/core";

import {
  getCountryRegulators,
  addCountryRegulator,
  updateCountryRegulator,
} from "../../../../../../API/Country";

import CountryRegulatorsTable from "./CountryRegulatorsTable";
import CountryRegulatorForm from "./CountryRegulatorForm";
import LoadingIndicator from "../../../../../../Component/LoadingIndicator";
import ModalDialog from "../ModalDialog";
import { recordTypes } from "../../../../../../API/config";
import { styles } from "./styles";
import ModalDialogContext from "../../../../../../Context/ModalDialog/ModalDialogContext";
import { useTranslation } from "react-i18next";

const CountryRegulators = (props) => {
  const { classes, hasPermission } = props;
  const [regulators, setRegulators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formState, setFormState] = useState({});
  const [canEdit, setCanEdit] = useState();
  const [canAdd, setCanAdd] = useState();
  const [formErrors, setFormErrors] = useState({});

  const { showMessage, showErrorMessage, showLoadingMessage } =
    useContext(ModalDialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    const evalPermissions = async () => {
      const canEdit = await hasPermission(
        recordTypes.countryRegulator.name,
        recordTypes.countryRegulator.actions.update.name,
      );
      setCanEdit(canEdit);
      const canAdd = await hasPermission(
        recordTypes.countryRegulator.name,
        recordTypes.countryRegulator.actions.create.name,
      );
      setCanAdd(canAdd);
    };

    evalPermissions();
  }, []);

  useEffect(() => {
    loadRegulators();
  }, []);

  const loadRegulators = async () => {
    try {
      setLoading(true);
      const result = await getCountryRegulators();
      const { data = [] } = result;
      setRegulators(data);
      setLoading(false);
    } catch (error) {
      showErrorMessage({ error });
      setLoading(false);
    }
  };

  const openAddRegulatorDialog = () => {
    setFormErrors({});
    setIsAddDialogOpen(true);
  };

  const closeAddRegulatorDialog = () => {
    setIsAddDialogOpen(false);
  };

  const isInvalid = (regulator) => {
    const errors = {};
    setFormErrors(errors);

    let hasError = false;

    if (regulator.country_id === null || regulator.country_id === undefined) {
      errors["country_id"] = t("messages.country.countryNameError");
      hasError = true;
    }

    if (
      !regulator.regulator_name ||
      typeof regulator.regulator_name !== "string"
    ) {
      errors["regulator_name"] = t("messages.country.regulatorNameError");
      hasError = true;
    }

    if (regulator.regulator_code && regulator.regulator_code.length > 4) {
      errors["regulator_code"] = t("messages.country.regulatorCodeError");
      hasError = true;
    }

    return hasError ? errors : null;
  };

  const submitNewRegulator = async () => {
    try {
      const errors = isInvalid(formState);
      if (errors) {
        setFormErrors(errors);
        return;
      }

      showLoadingMessage();
      const data = {
        id: formState.id,
        countryId: formState.country_id,
        name: formState.regulator_name,
        code: formState.regulator_code,
        isEquivalent: formState.is_equivalent,
      };

      await addCountryRegulator(data);
      await loadRegulators();

      setIsAddDialogOpen(false);
      showMessage({ message: t("messages.country.regulatorCreated") });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const updateRegulator = async (regulator) => {
    try {
      const errors = isInvalid(regulator);
      if (errors) {
        let message = "";
        Object.keys(errors).map(function (key, index) {
          message = message ? `${message}\n${errors[key]}` : `${errors[key]}`;
        });
        showMessage({
          message: message,
        });
        return false;
      }

      const data = {
        id: regulator.id,
        countryId: regulator.country_id,
        name: regulator.regulator_name,
        code: regulator.requlator_code,
        isEquivalent:
          regulator.is_equivalent === 1 || regulator.is_equivalent === "true"
            ? 1
            : 0,
      };

      await updateCountryRegulator(data);
      await loadRegulators();
      showMessage({ message: t("messages.country.regulatorUpdated") });
      return true;
    } catch (error) {
      showErrorMessage({ error });
      return false;
    }
  };

  const onFieldChange = (value, name) => {
    setFormState((c) => {
      c[name] = value;
      return c;
    });
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <>
      {loading ? (
        renderLoadingIndicator()
      ) : (
        <div className={classes.tableContainer}>
          <CountryRegulatorsTable
            loading={loading}
            regulators={regulators}
            onNewAction={openAddRegulatorDialog}
            onUpdateRecord={updateRegulator}
            canAdd={canAdd}
            canEdit={canEdit}
          />
        </div>
      )}

      <ModalDialog
        open={isAddDialogOpen}
        onClose={closeAddRegulatorDialog}
        onSubmit={submitNewRegulator}
        title={t("labels.country.regulatorList.addNewRegulator")}
      >
        <CountryRegulatorForm
          onFieldChange={onFieldChange}
          formErrors={formErrors}
        />
      </ModalDialog>
    </>
  );
};

export default withStyles(styles, { withTheme: true })(CountryRegulators);
